import React, { useEffect, useState, useCallback } from "react";
import { useOutletsQuery } from "../../../Store/Apis/outletApi";
import { useCustomerOrderReportQuery } from "../../../Store/Apis/reportApi";
import getStatusColor from "../../../Hooks/GetStatusColor";
import downloadPDF from "../../../Hooks/downloadReportPdf";
import { Link } from "react-router-dom";
import { isDropdownPermission } from "../../../Hooks/permissionsMiddleware";
import { useSelector } from "react-redux";
import formatDate from "../../../Hooks/formatDate";
import Loader from "../../../Components/Loader/Loader";
import Error from "../../../Components/Error/Error";
import { FaRegFilePdf, FaFileExcel, FaSearch } from "react-icons/fa";
import * as XLSX from "xlsx";

// Helper function to get formatted date (YYYY-MM-DD)
const getFormattedDate = (date) => {
  return date.toISOString().split("T")[0];
};
const CustomerOrders = () => {
  const user = useSelector((state) => state.auth.user);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  const [startDate, setStartDate] = useState(getFormattedDate(yesterday));
  const [endDate, setEndDate] = useState(getFormattedDate(today));
  const [keyID, setKeyID] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [customer, setCustomer] = useState("");

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Handle search submit on Enter key
  const handleSearchSubmit = useCallback(
    (e) => {
      if (e.key === "Enter") {
        setDebouncedSearchTerm(searchTerm);
      }
    },
    [searchTerm],
  );

  // Clear search
  const handleClearSearch = useCallback(() => {
    setSearchTerm("");
    setDebouncedSearchTerm("");
  }, []);

  const outletList = useOutletsQuery();

  const {
    data: Report,
    isSuccess,
    isLoading,
    isFetching,
    isError,
  } = useCustomerOrderReportQuery(
    {
      startDate,
      endDate,
      keyID,
      // Don't send customerName to API, handle client-side
    },
    { skip: !startDate },
  );

  // Client-side filtering for customer search
  const filteredOrders = React.useMemo(() => {
    if (!Report?.data?.orderDetails) return [];

    return Report.data.orderDetails.filter((order) => {
      // Customer name search - case insensitive, trim whitespace
      let customerMatch = true;
      if (debouncedSearchTerm) {
        const searchTerm = debouncedSearchTerm.toLowerCase().trim();
        const customerName = (order.customerName || "").toLowerCase().trim();
        const customerEmail = (order.customerEmail || "").toLowerCase().trim();
        const customerPhone = (order.customerPhone || "").toLowerCase().trim();

        customerMatch =
          customerName.includes(searchTerm) ||
          customerEmail.includes(searchTerm) ||
          customerPhone.includes(searchTerm);
      }

      return customerMatch;
    });
  }, [Report?.data?.orderDetails, debouncedSearchTerm]);

  // Calculate totals from filtered orders (matches displayed data)
  const computedTotals = React.useMemo(() => {
    if (!filteredOrders || filteredOrders.length === 0) {
      return { totalOrders: 0, totalOrderAmount: 0, totalReceivedAmount: 0 };
    }
    return {
      totalOrders: filteredOrders.length,
      totalOrderAmount: filteredOrders.reduce(
        (sum, item) => sum + (Number(item.orderAmount) || 0),
        0,
      ),
      totalReceivedAmount: filteredOrders.reduce(
        (sum, item) => sum + (Number(item.paidAmount) || 0),
        0,
      ),
    };
  }, [filteredOrders]);

  // PAGINATION CODE HERE
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const totalPages =
    filteredOrders.length > 0
      ? Math.ceil(filteredOrders.length / itemsPerPage)
      : 0;
  const paginatedData = filteredOrders.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm]);

  const renderPagination = () => {
    const pages = [];
    const pageLimit = 5;
    const startPage = Math.max(2, currentPage - 2);
    const endPage = Math.min(totalPages - 1, currentPage + 2);

    if (totalPages > 1) {
      pages.push(
        <li
          key={1}
          className={`page-item ${1 === currentPage ? "active" : ""}`}
        >
          <button className="page-link" onClick={() => handlePageChange(1)}>
            1
          </button>
        </li>,
      );

      if (startPage > 2) {
        pages.push(
          <li key="start-ellipsis" className="page-item disabled">
            <span className="page-link">...</span>
          </li>,
        );
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(
          <li
            key={i}
            className={`page-item ${i === currentPage ? "active" : ""}`}
          >
            <button className="page-link" onClick={() => handlePageChange(i)}>
              {i}
            </button>
          </li>,
        );
      }

      if (endPage < totalPages - 1) {
        pages.push(
          <li key="end-ellipsis" className="page-item disabled">
            <span className="page-link">...</span>
          </li>,
        );
      }

      pages.push(
        <li
          key={totalPages}
          className={`page-item ${totalPages === currentPage ? "active" : ""}`}
        >
          <button
            className="page-link"
            onClick={() => handlePageChange(totalPages)}
          >
            {totalPages}
          </button>
        </li>,
      );
    }

    return pages;
  };

  // GENERATE PDF CODE
  const outlet =
    keyID &&
    outletList?.data?.data.find((elem) => elem.outletId === Number(keyID));
  const outletName = outlet?.outletName || "";
  const rows =
    Array.isArray(filteredOrders) &&
    filteredOrders.map((item, index) => {
      const customerInfo =
        `${item.customerName || ""}
` +
        `${item.customerPhone || ""}
` +
        `${item.customerEmail || ""}
` +
        `${item.customerAddress || ""}`;

      return [
        index + 1,
        formatDate(item.date),
        item.orderId,
        customerInfo,
        "R " + item.orderAmount,
        "R " + item.paidAmount,
        item.outletName,
        item.statusName,
      ];
    });
  const columns = [
    "Sl No.",
    "Date",
    "Order ID",
    "Customer",
    "Order Amount",
    "Paid Amount",
    "Outlet",
    "Status",
  ];

  useEffect(() => {
    if (!isDropdownPermission(user.role_Id)) {
      setKeyID(user.outletID);
    }
  }, []);

  const downloadExcel = () => {
    if (
      !filteredOrders ||
      !Array.isArray(filteredOrders) ||
      filteredOrders.length === 0
    ) {
      alert("No data available for download");
      return;
    }

    // Prepare data for Excel
    const excelData = filteredOrders.map((item, index) => {
      const customerInfo =
        `${item.customerName || ""}\n` +
        `${item.customerPhone || ""}\n` +
        `${item.customerEmail || ""}\n` +
        `${item.customerAddress || ""}`;

      return {
        "Sl No.": index + 1,
        Date: formatDate(item.date),
        "Order Id": item.orderId || "",
        "Customer Details": customerInfo,
        "Order Amount": `R ${item.orderAmount.toFixed(2)}`,
        "Paid Amount": `R ${item.paidAmount.toFixed(2)}`,
        "Outlet Name": item.outletName || "",
        Status: item.statusName || "",
      };
    });

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // Set column widths for better formatting
    const columnWidths = [
      { wch: 8 }, // Sl No.
      { wch: 12 }, // Date
      { wch: 15 }, // Order Id
      { wch: 25 }, // Customer Details
      { wch: 15 }, // Order Amount
      { wch: 15 }, // Paid Amount
      { wch: 20 }, // Outlet Name
      { wch: 12 }, // Status
    ];
    worksheet["!cols"] = columnWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, "Customer Orders Report");

    // Generate filename with date range
    const filename = `Customer_Orders_Report_${formatDate(
      startDate,
    )}-${formatDate(endDate)}.xlsx`;

    // Download the file
    XLSX.writeFile(workbook, filename);
  };

  return (
    <div>
      <div className="row align-items-center justify-content-between mb-4">
        <div className="col">
          <h5 className="fw-500 text-white">Customer Order Report</h5>
        </div>
      </div>
      <div className="card mb-4">
        <div className="card-header p-4">
          <div className="row">
            <div className="col-md-3 col-sm-3">
              <label>Start Date</label>
              <input
                type="date"
                className="form-control"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="col-md-3 col-sm-3">
              <label>End Date</label>
              <input
                type="date"
                className="form-control"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            <div className="col-md-3 col-sm-3">
              <label>Outlet name</label>
              <select
                className="form-select"
                value={keyID}
                onChange={(e) => setKeyID(e.target.value)}
                disabled={
                  !outletList.isSuccess || !isDropdownPermission(user.role_Id)
                }
              >
                <option value="">All Outlets</option>
                {outletList?.data?.data.map((elem) => (
                  <option key={elem.outletId} value={elem.outletId}>
                    {elem.outletName}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label>Customer/Company</label>
              <div className="position-relative">
                <input
                  type="text"
                  name="customerName"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={handleSearchSubmit}
                  className="form-control pe-5"
                  placeholder="Enter Customer Name/Email/Phone"
                />
                <FaSearch
                  className="position-absolute top-50 translate-middle-y text-muted"
                  style={{ right: "15px", fontSize: "14px" }}
                />
              </div>
              {debouncedSearchTerm && (
                <small className="text-primary mt-1 d-block">
                  Searching for: "{debouncedSearchTerm}" (
                  {filteredOrders.length} matches)
                </small>
              )}
            </div>
          </div>
        </div>
        <div className="card-body p-0">
          {isLoading && <Loader height={500} />}
          {isError && <Error height={500} />}
          {isSuccess && (
            <>
              <div className="table-responsive mb-0">
                <table className="table table-bordered align-items-center mb-0">
                  <thead className="bg-light">
                    <tr>
                      <th
                        style={{ width: "10%" }}
                        className="text-uppercase text-secondary text-xs opacity-7"
                      >
                        Date
                      </th>
                      <th
                        style={{ width: "15%" }}
                        className="text-uppercase text-secondary text-xs opacity-7"
                      >
                        Order Id
                      </th>
                      <th
                        style={{ width: "15%" }}
                        className="text-uppercase text-secondary text-xs  opacity-7"
                      >
                        Customer / Company Details
                      </th>
                      <th
                        style={{ width: "15%" }}
                        className="text-uppercase text-secondary text-xs  opacity-7"
                      >
                        Order Amount
                      </th>
                      <th
                        style={{ width: "15%" }}
                        className="text-uppercase text-secondary text-xs  opacity-7"
                      >
                        Paid Amount
                      </th>
                      {/* <th style={{width: "15%"}} className="text-uppercase text-secondary text-xs  opacity-7">Outlet Name</th> */}
                      <th
                        style={{ width: "15%" }}
                        className="text-uppercase text-secondary text-xs opacity-7"
                      >
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedData.length > 0 ? (
                      paginatedData.map((item, index) => (
                        <tr key={index}>
                          <td className="text-sm px-4">
                            {formatDate(item.date)}
                          </td>
                          <td className="text-sm px-4">{item.orderId}</td>
                          <td className="text-sm px-4">
                            <p className="text-sm mb-0 fw-600">
                              {item.customerName}
                            </p>
                            <p className="text-xs mb-0">{item.customerPhone}</p>
                            <p className="text-xs mb-0">{item.customerEmail}</p>
                            <p className="text-xs mb-0">
                              {item.customerAddress}
                            </p>
                          </td>
                          <td className="text-sm px-4">
                            R {item.orderAmount.toFixed(2)}
                          </td>
                          <td className="text-sm px-4">
                            R {item.paidAmount.toFixed(2)}
                          </td>
                          {/* <td className="text-sm px-4">{item.outletName}</td> */}
                          <td className="text-sm px-4">
                            <span
                              type="button"
                              className={`badge badge-sm text-uppercase badge-${getStatusColor(
                                item.statusName,
                              )}`}
                              style={{ cursor: "default" }}
                            >
                              {item.statusName}
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={7} className="text-center py-5">
                          {debouncedSearchTerm ? (
                            <>
                              No orders found for "{debouncedSearchTerm}"
                              <br />
                              <small className="text-muted">
                                Try a different search term or{" "}
                                <button
                                  className="btn btn-link p-0 text-primary"
                                  onClick={handleClearSearch}
                                >
                                  clear search
                                </button>
                              </small>
                            </>
                          ) : (
                            "No Data Found !!"
                          )}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {paginatedData.length > 1 && (
                <>
                  <nav className="mt-4">
                    <ul className="pagination justify-content-center mb-4">
                      {renderPagination()}
                    </ul>
                  </nav>
                  <hr />
                </>
              )}
            </>
          )}

          <div className="row align-items-center px-4 my-3">
            <div className="col-md-7">
              <div className="row">
                <div className="col-md-3">
                  <span className="text-sm mb-0 fw-500">Orders:</span>
                  <span className="text-sm text-dark ms-2 fw-600 mb-0">
                    {computedTotals.totalOrders}
                  </span>
                </div>
                <div className="col-md-4">
                  <span className="text-sm mb-0 fw-500">Order Amount:</span>
                  <span className="text-sm text-dark ms-2 fw-600 mb-0">
                    R {computedTotals.totalOrderAmount.toFixed(2)}
                  </span>
                </div>
                <div className="col-md-4">
                  <span className="text-sm mb-0 fw-500">Received Amount:</span>
                  <span className="text-sm text-dark ms-2 fw-600 mb-0">
                    R {computedTotals.totalReceivedAmount.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
            <div className="col-5 text-end">
              <button
                className="btn btn-success me-2 mb-0"
                onClick={() =>
                  downloadPDF({
                    title: "Customer Order Report",
                    docName: `Customer Order_Report ${formatDate(
                      startDate,
                    )}-${formatDate(endDate)}`,
                    startDate: formatDate(startDate),
                    endDate: formatDate(endDate),
                    outletName,
                    rows,
                    columns,
                    totalOrder: computedTotals.totalOrders,
                    totalOrderAmout: computedTotals.totalOrderAmount.toFixed(2),
                    totalReciveAmout:
                      computedTotals.totalReceivedAmount.toFixed(2),
                    customerName: customer,
                  })
                }
                disabled={
                  !isSuccess ||
                  isLoading ||
                  isFetching ||
                  !Array.isArray(Report?.data.orderDetails)
                }
              >
                Download PDF <FaRegFilePdf size={20} />
              </button>

              <button
                className="btn btn-warning me-2"
                style={{ marginTop: "14px" }}
                onClick={downloadExcel}
                disabled={
                  !isSuccess ||
                  isLoading ||
                  isFetching ||
                  !Array.isArray(Report?.data.orderDetails)
                }
              >
                Download Excel <FaFileExcel size={20} />
              </button>
              {/* <Link
                to={`print?startDate=${startDate}&endDate=${endDate}&outlet=${keyID}`}
                target="_blank"
                disabled={!isSuccess || isLoading || isFetching ||  !Array.isArray(Report?.data.orderDetails)}
              >
                <button
                  rel="noopener noreferrer"
                  className="btn btn-warning mb-0"
                  disabled={!isSuccess || isLoading || isFetching ||  !Array.isArray(Report?.data.orderDetails)}
                >Print Report</button>
              </Link> */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerOrders;
